import { useEffect, useRef } from "react";

const VISIBLE_ROWS_DEBOUNCE_MS = 50;

const getVisibleProjectRefsFromGeometry = ({
  rowElements,
  viewport,
}: {
  rowElements: HTMLElement[];
  viewport: HTMLDivElement;
}) => {
  const viewportRect = viewport.getBoundingClientRect();

  return rowElements.flatMap((rowElement) => {
    const rowRect = rowElement.getBoundingClientRect();
    const isVisible =
      rowRect.bottom > viewportRect.top &&
      rowRect.top < viewportRect.bottom &&
      rowRect.right > viewportRect.left &&
      rowRect.left < viewportRect.right;
    const projectRef = rowElement.getAttribute("data-project-ref");

    return isVisible && projectRef ? [projectRef] : [];
  });
};

export const updateVisibleProjectRefs = ({
  entries,
  visibleProjectRefs,
}: {
  entries: IntersectionObserverEntry[];
  visibleProjectRefs: Set<string>;
}) => {
  const nextVisibleProjectRefs = new Set(visibleProjectRefs);

  entries.forEach((entry) => {
    const projectRef = entry.target.getAttribute("data-project-ref");
    if (!projectRef) return;

    if (entry.isIntersecting) {
      nextVisibleProjectRefs.add(projectRef);
    } else {
      nextVisibleProjectRefs.delete(projectRef);
    }
  });

  return nextVisibleProjectRefs;
};

export const useVisibleProjectRows = ({
  enabled,
  onVisibleProjectRefsChange,
  projectRefs,
  viewport,
}: {
  enabled: boolean;
  onVisibleProjectRefsChange: ({ projectRefs }: { projectRefs: string[] }) => void;
  projectRefs: string[];
  viewport: HTMLDivElement | null;
}) => {
  const visibleProjectRefs = useRef(new Set<string>());
  const latestProjectRefs = useRef(projectRefs);
  const latestChangeHandler = useRef(onVisibleProjectRefsChange);
  const projectRefsKey = JSON.stringify(projectRefs);

  useEffect(() => {
    latestProjectRefs.current = projectRefs;
  }, [projectRefs]);

  useEffect(() => {
    latestChangeHandler.current = onVisibleProjectRefsChange;
  }, [onVisibleProjectRefsChange]);

  useEffect(() => {
    visibleProjectRefs.current = new Set<string>();
    if (!enabled) return;
    if (!viewport) return;

    const rowElements = Array.from(
      viewport.querySelectorAll<HTMLElement>("[data-project-ref]"),
    );
    if (typeof IntersectionObserver === "undefined") {
      latestChangeHandler.current({
        projectRefs: getVisibleProjectRefsFromGeometry({
          rowElements,
          viewport,
        }),
      });
      return;
    }

    let changeTimeoutId: number | undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        visibleProjectRefs.current = updateVisibleProjectRefs({
          entries,
          visibleProjectRefs: visibleProjectRefs.current,
        });
        window.clearTimeout(changeTimeoutId);
        changeTimeoutId = window.setTimeout(() => {
          const orderedVisibleProjectRefs = latestProjectRefs.current.filter(
            (projectRef) => visibleProjectRefs.current.has(projectRef),
          );
          latestChangeHandler.current({ projectRefs: orderedVisibleProjectRefs });
        }, VISIBLE_ROWS_DEBOUNCE_MS);
      },
      { root: viewport },
    );

    rowElements.forEach((rowElement) => observer.observe(rowElement));

    return () => {
      window.clearTimeout(changeTimeoutId);
      observer.disconnect();
    };
  }, [enabled, projectRefsKey, viewport]);
};
